import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { RouterProvider } from 'react-router/dom';

import { getUserManager, onSigninCallback } from '@/auth/oidc';
import { loadConfig } from '@/config';
import { router } from '@/app/router';
import { prefetchAllRoutes } from '@/app/prefetchRoutes';
import { TopErrorBoundary } from '@/app/AppError';
import { installGlobalErrorReporting } from '@/lib/errorReporting';
import { I18nProvider, initI18n } from '@/i18n';
import PwaInstallPrompt from '@/components/PwaInstallPrompt';

import './index.css';

/**
 * Auto-recover from stale-deploy chunk errors. When a new build is deployed, its
 * content-hashed chunks replace the old ones; an ALREADY-OPEN session still holds
 * the old chunk names, so the next lazy route import 404s → Vite emits
 * `vite:preloadError`. Instead of dead-ending on "Failed to fetch dynamically
 * imported module", reload once to fetch the fresh index.html + chunks. The
 * sessionStorage timestamp guards against a reload loop if the reload also fails.
 */
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  const KEY = 'chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || '0');
  if (Date.now() - last > 10_000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
  }
});

/**
 * Register the hand-rolled service worker (public/sw.js) for offline app-shell
 * caching. Production-only so Vite's dev HMR is never intercepted; fully guarded
 * so an unsupporting browser just skips it (progressive enhancement).
 */
function registerServiceWorker() {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;
  const register = () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* SW registration failed — the SPA keeps working without offline cache */
    });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

async function bootstrap() {
  // Runtime config must be available before anything renders (auth, API).
  await loadConfig();

  // Apply the persisted UI language to <html lang/dir> before the first paint
  // (RTL for ar), so the shell renders in the right language + direction.
  initI18n();

  // Catch + report uncaught errors / unhandled rejections (prod-only email).
  installGlobalErrorReporting();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <TopErrorBoundary>
        <AuthProvider userManager={getUserManager()} onSigninCallback={onSigninCallback}>
          <I18nProvider>
            <RouterProvider router={router} />
            <PwaInstallPrompt />
          </I18nProvider>
        </AuthProvider>
      </TopErrorBoundary>
    </React.StrictMode>,
  );

  registerServiceWorker();

  // Warm all lazy route chunks in the background so navigation is instant and the
  // target page's controls are interactive on arrival — closes the cold-chunk
  // window behind the first-click-needs-two-clicks issue (BUG-13).
  prefetchAllRoutes(router.routes as never);
}

void bootstrap();
